import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StripeConnectButton } from "@/components/StripeConnectButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DollarSign, Users, TrendingUp, LogOut, Copy, Loader2, Plus, Trash2,
  Percent, FileText, ShieldAlert, CalendarClock, Link2, Activity, BadgeCheck, KeyRound,
  Rocket, AlertTriangle, ChevronLeft, ArrowRight,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Kopye!"); };

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#222222] bg-gradient-to-b from-[#101010] to-[#0F0F0F] rounded-2xl p-4" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[#888888] mb-1.5"><Icon className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className="font-heading text-xl font-bold" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [violations, setViolations] = useState([]);
  const [tab, setTab] = useState("overview");
  const [showPlan, setShowPlan] = useState(false);
  const [planForm, setPlanForm] = useState({ name: "", price: 0, days: 30, base_tier: "starter", active: true });
  const [busy, setBusy] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwDismissed, setPwDismissed] = useState(false);
  const [stripeEmail, setStripeEmail] = useState("");
  const [savingPayout, setSavingPayout] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, d, c, t, p, v] = await Promise.all([
        api.get("/partner/me"), api.get("/partner/dashboard"),
        api.get("/partner/customers"), api.get("/partner/transactions"),
        api.get("/partner/plans"), api.get("/partner/violations"),
      ]);
      setMe(m.data); setDash(d.data); setCustomers(c.data);
      setTransactions(t.data); setPlans(p.data); setViolations(v.data);
      setStripeEmail(m.data?.stripe_payout_email || "");
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePlan = async () => {
    if (!planForm.name.trim()) { toast.error("Antre yon non plan"); return; }
    setBusy(true);
    try {
      await api.post("/partner/plans", planForm);
      toast.success("Plan kreye"); setShowPlan(false);
      setPlanForm({ name: "", price: 0, days: 30, base_tier: "starter", active: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const delPlan = async (id) => {
    try { await api.delete(`/partner/plans/${id}`); toast.success("Efase"); load(); }
    catch (e) { toast.error("Erè"); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const savePayout = async () => {
    setSavingPayout(true);
    try {
      await api.post("/partner/payout-settings", { stripe_payout_email: stripeEmail });
      toast.success("Imèl Stripe anrejistre!");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPayout(false); }
  };

  const changePassword = async () => {
    if (newPass !== confirmPass) { toast.error("Modpas yo pa menm"); return; }
    setBusy(true);
    try {
      await api.post("/partner/change-password", { new_password: newPass });
      toast.success("Modpas chanje!");
      setNewPass(""); setConfirmPass("");
      setShowPw(false); setPwDismissed(true);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const mustChange = !!me?.must_change_password;
  const contractActive = me?.contract?.status === "active" && (me?.contract?.remaining_days ?? 0) > 0;
  const pwOpen = showPw || (mustChange && !pwDismissed);

  if (pwOpen) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col px-4" data-testid="force-password-screen">
        <div className="w-full max-w-md mx-auto pt-6">
          <button onClick={() => { setShowPw(false); setPwDismissed(true); }} data-testid="password-back-button"
            className="flex items-center gap-1.5 text-sm text-[#888888] hover:text-white transition-colors mb-6">
            <ChevronLeft className="h-4 w-4" /> Retounen nan dashboard
          </button>
        </div>
        <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-center pb-16">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="h-14 w-14 rounded-2xl bg-[#00F593]/15 flex items-center justify-center mb-4">
              <KeyRound className="h-7 w-7 text-[#00F593]" />
            </div>
            <h1 className="font-heading text-2xl font-bold">Chanje Modpas Ou</h1>
            <p className="text-sm text-[#888888] mt-2 leading-relaxed">
              {mustChange
                ? <>Nou rekòmande w chanje modpas tanporè a. Ou ka fè l kounye a oswa nenpòt lè nan dashboard ou.</>
                : <>Antre yon nouvo modpas.</>}
              <br />(6+ karaktè, gwo lèt, ti lèt, yon senbòl)
            </p>
          </div>
          <div className="border border-[#222222] bg-[#0F0F0F] rounded-3xl p-6 space-y-3">
            <Input data-testid="new-password" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Nouvo modpas" className="bg-[#050505] border-[#222222] h-12 rounded-xl" />
            <Input data-testid="confirm-password" type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} placeholder="Konfime modpas" className="bg-[#050505] border-[#222222] h-12 rounded-xl" />
            <Button data-testid="save-password" disabled={busy || !newPass} onClick={changePassword} className="w-full bg-white text-black hover:bg-[#e5e5e5] h-12 rounded-xl font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anrejistre Nouvo Modpas"}
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="min-w-0">
            <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#888888]">Partner Portal</div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl font-bold truncate max-w-[45vw] sm:max-w-none">{me?.name || user?.name}</h1>
              <span data-testid="partner-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-black shrink-0" style={{ backgroundColor: "#00F593" }}>
                <BadgeCheck className="h-3.5 w-3.5" /> PARTNER
              </span>
              {dash?.badge && (
                <span data-testid="partner-rank-badge" title={`#${dash.rank} sou ${dash.total_ranked} patnè`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-black shrink-0"
                  style={{ backgroundColor: dash.badge.color, boxShadow: `0 0 14px ${dash.badge.color}66` }}>
                  {dash.badge.label}
                </span>
              )}
            </div>
            {me?.company && <div className="text-xs text-[#888888] truncate">{me.company}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button data-testid="partner-change-password" aria-label="Chanje modpas" title="Chanje modpas" onClick={() => setShowPw(true)} variant="ghost" className="text-[#888888] hover:text-white h-9 px-2 sm:px-3">
              <KeyRound className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Modpas</span>
            </Button>
            <Button data-testid="partner-logout" aria-label="Dekonekte" title="Dekonekte" onClick={doLogout} variant="ghost" className="text-[#888888] hover:text-white h-9 px-2 sm:px-3">
              <LogOut className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Dekonekte</span>
            </Button>
          </div>
        </div>

        {/* Free full-app access (Elite) during the contract */}
        {contractActive ? (
          <div className="border border-[#00F593]/40 bg-[#00F593]/10 rounded-2xl p-4 mb-5 flex items-center justify-between gap-3" data-testid="partner-app-access">
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-[#00F593]" />Aksè Elite Konplè Aktif</div>
              <div className="text-xs text-[#888888] mt-0.5">Ou gen aksè konplè nan app la (tout 4 enstriman) pandan {me?.contract?.remaining_days} jou ki rete nan kontra a.</div>
            </div>
            <Button data-testid="partner-open-app" onClick={() => navigate("/app")} className="shrink-0 bg-white text-black hover:bg-[#e5e5e5] h-10 rounded-full px-4"><Rocket className="h-4 w-4 mr-1.5" />Ouvri App</Button>
          </div>
        ) : (
          <div className="border border-[#ff5b5b]/40 bg-[#ff5b5b]/10 rounded-2xl p-4 mb-5 flex items-start gap-3" data-testid="partner-contract-expired">
            <AlertTriangle className="h-5 w-5 text-[#ff5b5b] shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-sm font-bold text-[#ff9b9b]">Kontra ou fini</div>
              <div className="text-xs text-[#888888] mt-0.5">Aksè app la bloke pou kounye a. Tanpri kontakte CEO a pou renouvle kontra a epi reprann aksè Elite konplè ou an.</div>
            </div>
          </div>
        )}

        {/* Referral link */}
        <div className="border border-[#222222] bg-gradient-to-b from-[#101010] to-[#0F0F0F] rounded-2xl p-4 mb-5" data-testid="referral-card">
          <div className="flex items-center gap-1.5 text-[#888888] mb-2"><Link2 className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">Lyen Referral Ou</span></div>
          {me?.referral && (
            <>
              {/* Path link — recommended, always works */}
              <div className="flex items-center gap-2 bg-[#050505] border border-[#00F593]/50 px-3 py-2 rounded-lg mb-1.5" data-testid="ref-path-row">
                <span className="font-mono-term text-[8px] uppercase text-[#00F593] font-bold w-16 shrink-0 flex items-center gap-1"><BadgeCheck className="h-3 w-3" />Ofisyèl</span>
                <span className="text-xs truncate flex-1">{me.referral.path_link}</span>
                <button onClick={() => copy(me.referral.path_link)} className="text-[#00F593] hover:text-white" data-testid="copy-path-link"><Copy className="h-4 w-4" /></button>
              </div>
              <p className="text-[10px] text-[#888888] leading-relaxed mt-1.5">Sa se lyen referral ou — pataje l pou touche komisyon sou chak abònman.</p>
            </>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#101010] border border-[#222222] flex-wrap h-auto p-1">
            {[["overview", "Apèsi"], ["customers", "Kliyan"], ["transactions", "Tranzaksyon"], ["plans", "Plan"], ["contract", "Kontra"], ["payout", "Peman"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`ptab-${v}`} className="rounded-md text-xs px-3 h-8 data-[state=active]:bg-white data-[state=active]:text-black">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Metric icon={DollarSign} label="Total Revni" value={money(dash?.total_revenue)} accent="#00F593" />
              <Metric icon={Percent} label="Komisyon Ou" value={money(dash?.commission_earned)} accent="#00F593" />
              <Metric icon={CalendarClock} label="Revni Mwa" value={money(dash?.monthly_revenue)} />
              <Metric icon={Activity} label="Revni Jodi a" value={money(dash?.daily_revenue)} />
              <Metric icon={Users} label="Kliyan" value={dash?.customers ?? 0} />
              <Metric icon={TrendingUp} label="Abònman Aktif" value={dash?.active_subscriptions ?? 0} />
            </div>
            <div className="border border-[#222222] bg-[#101010] rounded-2xl p-4 flex justify-between text-sm">
              <span className="text-[#888888]">To konvèsyon</span><span className="font-heading font-bold">{dash?.conversion_rate ?? 0}%</span>
            </div>
            <div className="border border-[#222222] bg-[#101010] rounded-2xl p-4 flex justify-between text-sm">
              <span className="text-[#888888]">Pousantaj komisyon ou</span><span className="font-heading font-bold">{dash?.commission_pct ?? 0}%</span>
            </div>
            {!me?.stripe_payout_email && (
              <button onClick={() => setTab("payout")} data-testid="connect-stripe-prompt"
                className="w-full text-left border border-[#00F593]/40 bg-[#00F593]/10 rounded-2xl p-4 flex items-center gap-3 hover:bg-[#00F593]/15 transition-colors">
                <div className="h-10 w-10 rounded-xl bg-[#00F593]/20 flex items-center justify-center shrink-0">
                  <DollarSign className="h-5 w-5 text-[#00F593]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-white text-sm">Konekte Stripe pou resevwa % ou</div>
                  <div className="text-[11px] text-[#888888]">Ou poko konekte kont Stripe ou. Peze la pou ajoute l epi resevwa komisyon ou yo.</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#00F593] shrink-0" />
              </button>
            )}
          </TabsContent>

          <TabsContent value="customers" className="mt-4">
            {customers.length === 0 ? <Empty text="Poko gen kliyan atravè lyen ou." /> : (
              <div className="space-y-2" data-testid="customers-list">
                {customers.map((c, i) => (
                  <div key={i} className="border border-[#222222] bg-[#101010] rounded-xl p-3 flex justify-between">
                    <div className="min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-[#888888] truncate">{c.email}</div></div>
                    <div className="text-right shrink-0">
                      <div className={`text-xs font-semibold ${c.active ? "text-[#00F593]" : "text-[#888888]"}`}>{c.plan || "No plan"}</div>
                      <div className="font-mono-term text-[10px] text-[#888888]">{c.active ? `${c.days_left}j rete` : c.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            {transactions.length === 0 ? <Empty text="Poko gen tranzaksyon." /> : (
              <div className="space-y-2" data-testid="transactions-list">
                {transactions.map((t, i) => (
                  <div key={i} className="border border-[#222222] bg-[#101010] rounded-xl p-3 flex justify-between">
                    <div><div className="font-semibold text-sm">{t.plan_name}</div><div className="font-mono-term text-[10px] text-[#888888]">{(t.created_at || "").slice(0, 10)} · {t.status}</div></div>
                    <div className="text-right"><div className="font-heading text-sm font-bold">{money(t.amount)}</div><div className="font-mono-term text-[10px] text-[#00F593]">+{money(t.your_commission)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="plans" className="mt-4 space-y-3">
            <Button data-testid="new-plan-button" onClick={() => setShowPlan(true)} className="w-full bg-white text-black hover:bg-[#e5e5e5] h-10"><Plus className="h-4 w-4 mr-1" />Kreye Plan</Button>
            {plans.length === 0 ? <Empty text="Poko gen plan. Kreye pwòp plan ou." /> : (
              <div className="space-y-2" data-testid="plans-list">
                {plans.map((p) => (
                  <div key={p.id} className="border border-[#222222] bg-[#101010] rounded-xl p-3 flex justify-between items-center">
                    <div><div className="font-semibold text-sm">{p.name}</div><div className="font-mono-term text-[10px] text-[#888888]">{money(p.price)} · {p.days} jou · {p.base_tier}{!p.active && " · inaktif"}</div></div>
                    <button onClick={() => delPlan(p.id)} className="text-[#ff5b5b] hover:text-[#ff8b8b]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contract" className="mt-4 space-y-3">
            <div className="border border-[#222222] bg-[#101010] rounded-2xl p-4" data-testid="contract-view">
              <div className="flex items-center gap-1.5 text-[#888888] mb-3"><FileText className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">Kontra Patenarya</span></div>
              <Row label="Tip kontra" value={me?.contract?.label} />
              <Row label="Dat kòmanse" value={(me?.contract?.start_date || "").slice(0, 10)} />
              <Row label="Dat ekspirasyon" value={(me?.contract?.end_date || "").slice(0, 10)} />
              <Row label="Jou ki rete" value={`${me?.contract?.remaining_days ?? "—"} jou`} />
              <Row label="Estati" value={me?.contract?.status} accent={me?.contract?.status === "active" ? "#00F593" : "#ff5b5b"} />
              <p className="text-[10px] text-[#888888] mt-3 leading-relaxed">Règ patenarya: ou ka jere pwòp kliyan, plan ak revni ou. Ou pa ka modifye kontra a — kontakte CEO a pou nenpòt chanjman.</p>
            </div>
            <div className="border border-[#222222] bg-[#101010] rounded-2xl p-4" data-testid="partner-violations">
              <div className="flex items-center gap-1.5 text-[#888888] mb-3"><ShieldAlert className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">Konfòmite</span></div>
              {violations.length === 0 ? <div className="text-xs text-[#00F593]">Okenn vyolasyon. Kontinye bon travay la!</div> : (
                <div className="space-y-2">
                  {violations.map((v) => (
                    <div key={v.id} className="text-xs border-b border-[#222222] pb-1.5">
                      <div className="flex justify-between"><span className="font-semibold text-[#ff9b9b]">{v.type}</span><span className="text-[10px] uppercase text-[#888888]">{v.action}</span></div>
                      <div className="text-[#888888]">{v.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payout" className="mt-4 space-y-3" data-testid="partner-payout-tab">
            <div className="border border-[#222222] bg-[#101010] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1.5">
                <DollarSign className="h-4 w-4 text-[#00F593]" />
                <h3 className="font-heading font-semibold">Konekte Stripe pou Resevwa Peman</h3>
              </div>
              <p className="text-xs text-[#888888] leading-relaxed mb-4">
                Nou voye komisyon ou yo sou kont <b className="text-white">Stripe</b> ou. Konekte kont ou an yon sèl fwa
                pou resevwa peman otomatikman.
              </p>

              <div className="space-y-2.5 mb-5">
                {[
                  ["1", "Kreye yon kont Stripe", "Ale sou stripe.com epi kreye yon kont (gratis). Chwazi peyi w epi verifye idantite w."],
                  ["2", "Ajoute kont labank ou", "Nan Stripe, konekte kont labank ou oswa kat debi w pou w ka retire lajan."],
                  ["3", "Bay imèl Stripe ou", "Antre menm imèl kont Stripe ou anba a epi anrejistre l."],
                  ["4", "Resevwa peman ou", "Chak fwa yon kliyan ou peye, komisyon ou an ap voye sou Stripe ou (dapre orè peman CEO a)."],
                ].map(([n, t, d]) => (
                  <div key={n} className="flex gap-3" data-testid={`payout-step-${n}`}>
                    <span className="h-6 w-6 rounded-full bg-[#00F593]/15 text-[#00F593] text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
                    <div>
                      <div className="text-sm font-semibold text-white">{t}</div>
                      <div className="text-[11px] text-[#888888] leading-relaxed">{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888888]">Konekte Kont Stripe ou</label>
              <div className="mt-2 mb-4">
                <StripeConnectButton variant="partner" />
              </div>

              <div className="border-t border-[#222222] pt-4">
                <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888888]">Oswa bay imèl Stripe ou (backup)</label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="stripe-payout-email" type="email" value={stripeEmail}
                    onChange={(e) => setStripeEmail(e.target.value)} placeholder="imel-stripe@egzanp.com"
                    className="bg-[#050505] border-[#222222] h-11 rounded-xl flex-1" />
                  <Button data-testid="save-payout-email" onClick={savePayout} disabled={savingPayout}
                    className="bg-[#222222] text-white hover:bg-[#333333] h-11 rounded-xl px-5">
                    {savingPayout ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anrejistre"}
                  </Button>
                </div>
                {me?.stripe_payout_email && (
                  <p className="text-[11px] text-[#00F593] mt-2 flex items-center gap-1.5" data-testid="payout-saved-note">
                    <BadgeCheck className="h-3.5 w-3.5" /> Imèl Stripe: {me.stripe_payout_email}
                  </p>
                )}
              </div>
              <p className="text-[10px] text-[#888888] mt-3 leading-relaxed">
                Bezwen èd? Kontakte CEO a nan app la. Peman yo trete manyèlman dapre kontra patenarya ou.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Mandatory first-login password change is handled full-screen above (see mustChange). */}

      {/* Plan dialog */}
      <Dialog open={showPlan} onOpenChange={setShowPlan}>
        <DialogContent className="bg-[#0F0F0F] border-[#222222] text-white max-w-sm">
          <DialogHeader><DialogTitle>Nouvo Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="plan-name" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Non plan" className="bg-[#050505] border-[#222222] h-9" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="plan-price" type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: Number(e.target.value) })} placeholder="Pri $" className="bg-[#050505] border-[#222222] h-9" />
              <Input data-testid="plan-days" type="number" value={planForm.days} onChange={(e) => setPlanForm({ ...planForm, days: Number(e.target.value) })} placeholder="Jou" className="bg-[#050505] border-[#222222] h-9" />
            </div>
            <select data-testid="plan-tier" value={planForm.base_tier} onChange={(e) => setPlanForm({ ...planForm, base_tier: e.target.value })} className="w-full bg-[#050505] border border-[#222222] h-9 rounded px-2 text-sm">
              <option value="test">Test</option><option value="starter">Starter</option>
              <option value="pro">Pro</option><option value="elite">Elite</option>
            </select>
            <DialogFooter>
              <Button data-testid="save-plan" disabled={busy} onClick={savePlan} className="w-full bg-[#00F593]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kreye"}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, accent }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-[#888888]">{label}</span><span className="font-semibold" style={accent ? { color: accent } : {}}>{value || "—"}</span></div>;
}
function Empty({ text }) {
  return <div className="text-center py-10 text-[#888888] text-sm">{text}</div>;
}
